// PhotoPickerLogic.ts

import { useContext, useRef, useState } from "react";
import { SessionContext } from "../../../utility/useSession";
import { getAPIRootUrl, getFileUrl } from "../../../utility/utils";
import { useTranslation } from "react-i18next";

export const usePhotoPickerLogic = (
  fileName: string,
  width: number,
  height: number,
  scale: number,
  onUpdated: (fileName: string) => void,
  ignoreSizeConstraints?: boolean
) => {
  const session = useContext(SessionContext);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const { t } = useTranslation();

  const url = uploading ? "" : getFileUrl(session, fileName);
  const inputRef = useRef<HTMLInputElement>(null);

  const onFileSelected = (file: File) => {
    setUploadErr(null);

    const limit = 500 * 1024; // 500KB

    if (file.size > limit) {
      const errMsg = t("upsells.fileExcLimit");
      setUploadErr(errMsg);
      return;
    }

    const uploadFileBytes = async () => {
      try {
        setUploading(true);
        const bytes = await file.arrayBuffer();
        const apiUrl = getAPIRootUrl(session);
        const result = await fetch(`${apiUrl}/advert/upload`, {
          method: "POST",
          body: bytes,
          headers: session.current.context.headers,
        });
        if (result.status !== 200) {
          throw await result.text();
        } else {
          const fileRsp: { fileName: string } = await result.json();
          if (fileRsp?.fileName == null) return;
          onUpdated(fileRsp?.fileName);
        }
      } catch (error) {
        const errMsg = t("upsells.errorUploading", { err: error });
        console.log(errMsg);
        setUploadErr(errMsg);
      }
      setUploading(false);
    };

    // Validate file is an image and is in the correct resolution
    const img = new Image();
    const _URL = window.URL || window.webkitURL;
    const objectUrl = _URL.createObjectURL(file);
    img.onload = function () {
      const imgWidth = img.width ?? 0;
      const imgHeight = img.height ?? 0;
      _URL.revokeObjectURL(objectUrl);

      if (!ignoreSizeConstraints && (imgWidth !== width || imgHeight !== height)) {
        const errMsg = t("upsells.incorrectDimensions", { incWidth: imgWidth, incHeight: imgHeight, correctWidth: width, correctHeight: height });

        setUploadErr(errMsg);
        return;
      }
      uploadFileBytes();
    };
    img.onerror = function (error) {
      _URL.revokeObjectURL(objectUrl);
      const errMsg = t("upsells.error", { err: error });

      setUploadErr(errMsg);
    };
    img.src = objectUrl;
  };

  return {
    url,
    uploading,
    uploadErr,
    inputRef,
    onFileSelected,
  };
};
